/**
 * Grace period derivation (docs/04 §3).
 * Takes authoritative observation inputs — never estimated balances.
 * rankCards() must receive a precomputed CardEligibilityContext; it must not call deriveGrace.
 */

import type { Cents } from './money';
import { businessDate, compareBusinessDates, type BusinessDate } from './time';
import { observationBusinessDate, type CardPaymentInput } from './state';
import {
  closedCyclesSortedDesc,
  derivedPaidInFullByDueDate,
  statementHistoryStale,
  type PaymentAllocationInput,
  type PaymentHistoryCoverageInput,
  type StatementCycleInput,
} from './cycles';

export type GraceStatus =
  | 'active'
  | 'inactive'
  | 'likely_active'
  | 'likely_inactive'
  | 'restoration_pending'
  | 'unknown'
  | 'not_applicable';

export type GraceRestoreRule =
  | 'immediate_on_full_payoff'
  | 'next_billing_cycle_after_full_payoff'
  | 'after_one_full_statement_cycle'
  | 'after_two_full_statement_cycles'
  | 'issuer_confirmation_required'
  | 'unknown';

export type GraceConfirmationInput = {
  id: string;
  card_id: string;
  deleted_at: string | null;
  confirmed_status: GraceStatus;
  reason: string;
  confirmed_at: string;
  expires_at: string;
};

export type ReviewItemInput = {
  id: string;
  card_id: string | null;
  deleted_at: string | null;
  affects_grace: boolean;
  resolved_at: string | null;
  created_at: string;
};

export type GraceCardInput = {
  id: string;
  status: string;
  grace_exists: boolean | null;
  grace_restore: GraceRestoreRule;
};

export type DeriveGraceInput = {
  card: GraceCardInput;
  confirmation: GraceConfirmationInput | null;
  cycles: readonly StatementCycleInput[];
  payments: readonly CardPaymentInput[];
  allocations: readonly PaymentAllocationInput[];
  coverage: readonly PaymentHistoryCoverageInput[];
  reviewItems: readonly ReviewItemInput[];
  /** Newest account_restricted=true observation after a confirmation, if any. */
  accountRestrictedAfter: string | null;
  asOf: BusinessDate;
  /**
   * Authoritative observed current balance (never the estimate).
   * Used only to assert the "new purchases ≠ grace loss" rule in tests/docs;
   * the algorithm does not treat a non-zero balance as a breach.
   */
  observedCurrentBalanceCents: Cents | null;
};

export type GraceResult = {
  status: GraceStatus;
  reason: string;
  derivedFrom: 'issuer_confirmation' | 'derivation';
  /**
   * Due date of the statement paid in full when status is `active` from that path.
   * Null when unknown — Stage 7 must not emit GRACE_OK without this.
   */
  paid_in_full_due_date: string | null;
  /** Present when a positive confirmation was superseded. */
  warning?: 'GRACE_CONFIRMATION_SUPERSEDED';
};

const NEGATIVE_CONFIRMATIONS = new Set<GraceStatus>([
  'inactive',
  'likely_inactive',
  'restoration_pending',
  'not_applicable',
]);
const POSITIVE_CONFIRMATIONS = new Set<GraceStatus>(['active', 'likely_active']);

function paidInFull(cycle: StatementCycleInput, input: DeriveGraceInput): boolean | null {
  return derivedPaidInFullByDueDate(cycle, input.payments, input.allocations, input.coverage);
}

function newestUnexpiredConfirmation(
  cardId: string,
  confirmation: GraceConfirmationInput | null,
  asOf: BusinessDate,
): GraceConfirmationInput | null {
  if (!confirmation || confirmation.deleted_at !== null) return null;
  if (confirmation.card_id !== cardId) return null;
  if (compareBusinessDates(businessDate(confirmation.expires_at), asOf) < 0) return null;
  return confirmation;
}

function positiveConfirmationUsable(
  conf: GraceConfirmationInput,
  closed: StatementCycleInput[],
  input: DeriveGraceInput,
): boolean {
  const confirmedDay = observationBusinessDate(conf.confirmed_at);

  for (const cycle of closed) {
    if (cycle.payment_due_date === null) continue;
    // Case 30: payment_due_date >= date(confirmed_at), not > — a confirmation on the
    // morning of the due date is superseded if payment is missed that evening.
    if (compareBusinessDates(businessDate(cycle.payment_due_date), confirmedDay) < 0) {
      continue;
    }
    if (paidInFull(cycle, input) === false) return false;
    if (cycle.status === 'past_due' && paidInFull(cycle, input) !== true) return false;
  }

  if (input.accountRestrictedAfter !== null && input.accountRestrictedAfter > conf.confirmed_at) {
    return false;
  }
  if (input.card.status !== 'open') return false;

  for (const item of input.reviewItems) {
    if (item.deleted_at !== null) continue;
    if (item.card_id !== null && item.card_id !== input.card.id) continue;
    if (!item.affects_grace || item.resolved_at !== null) continue;
    if (item.created_at > conf.confirmed_at) return false;
  }

  return true;
}

/**
 * First matching branch returns. Order is load-bearing (docs/04 §3.2).
 */
export function deriveGrace(input: DeriveGraceInput): GraceResult {
  const { card, asOf } = input;
  void input.observedCurrentBalanceCents; // authoritative input; not a breach signal

  // 1. Confirmations
  const conf = newestUnexpiredConfirmation(card.id, input.confirmation, asOf);
  if (conf !== null) {
    if (NEGATIVE_CONFIRMATIONS.has(conf.confirmed_status)) {
      return {
        status: conf.confirmed_status,
        reason: conf.reason,
        paid_in_full_due_date: null,
        derivedFrom: 'issuer_confirmation',
      };
    }
    if (POSITIVE_CONFIRMATIONS.has(conf.confirmed_status)) {
      const closedForConf = closedCyclesSortedDesc(card.id, input.cycles);
      if (positiveConfirmationUsable(conf, closedForConf, input)) {
        return {
          status: conf.confirmed_status,
          reason: conf.reason,
          paid_in_full_due_date: null,
          derivedFrom: 'issuer_confirmation',
        };
      }
      // Fall through with superseded warning attached at the end of derivation if needed.
    }
  }

  // 2. grace_exists === false
  if (card.grace_exists === false) {
    return {
      status: 'not_applicable',
      reason: "This account's agreement states purchases do not receive a grace period.",
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  // 3. Statement history presence / freshness
  const allLiveCycles = input.cycles.filter((c) => c.card_id === card.id && c.deleted_at === null);
  if (allLiveCycles.length === 0) {
    return {
      status: 'unknown',
      reason: 'No statement history on record for this account.',
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  const closed = closedCyclesSortedDesc(card.id, input.cycles);
  if (closed.length === 0) {
    return {
      status: 'unknown',
      reason: 'No completed statement cycle on record for this account.',
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  const newestClosed = closed[0];
  if (newestClosed === undefined) {
    return {
      status: 'unknown',
      reason: 'No completed statement cycle on record for this account.',
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  if (statementHistoryStale(newestClosed, asOf)) {
    return {
      status: 'unknown',
      reason: `Statement history has not been updated since ${newestClosed.cycle_end_date}.`,
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  // 5. Delinquency / breach first
  const breach = closed.find((cycle) => {
    if (cycle.statement_balance_cents === null || cycle.statement_balance_cents <= 0n) {
      return false;
    }
    return paidInFull(cycle, input) === false;
  });

  if (breach !== undefined) {
    if (card.grace_restore === 'unknown' || card.grace_restore === 'issuer_confirmation_required') {
      const stmt = breach.statement_date ?? breach.cycle_end_date;
      return {
        status: 'unknown',
        reason: `This account carried a balance through its ${stmt} statement. The agreement's grace-restoration rule has not been recorded, so whether new purchases are interest-free cannot be determined. Confirm with the issuer before using this card.`,
        paid_in_full_due_date: null,
        derivedFrom: 'derivation',
      };
    }

    // Restoration paths for known policies (Task 4b needs the unknown branch above;
    // remaining restoration math is implemented for correctness of later cases).
    const payoffConfirmed = breachObligationPaid(breach, closed, input);
    const restored = isRestored(card.grace_restore, payoffConfirmed, breach, closed, input);
    const qualifying = countQualifyingAfter(breach, closed, input);
    const progressed = payoffConfirmed || qualifying > 0;
    const newerBreach = closed.find(
      (c) =>
        c.id !== breach.id &&
        compareBusinessDates(businessDate(c.cycle_end_date), businessDate(breach.cycle_end_date)) >
          0 &&
        c.statement_balance_cents !== null &&
        c.statement_balance_cents > 0n &&
        paidInFull(c, input) === false,
    );

    if (restored && newerBreach === undefined) {
      return {
        status: 'active',
        reason: `Grace restored after the ${breach.statement_date ?? breach.cycle_end_date} statement breach under ${card.grace_restore}.`,
        paid_in_full_due_date: null,
        derivedFrom: 'derivation',
      };
    }
    if (progressed && !restored) {
      return {
        status: 'restoration_pending',
        reason: `The ${breach.statement_date ?? breach.cycle_end_date} statement balance ${
          payoffConfirmed ? 'has been paid off' : 'is still being repaid'
        }, but this account's ${card.grace_restore} requirement is not yet satisfied. New purchases may still accrue interest from the transaction date.`,
        paid_in_full_due_date: null,
        derivedFrom: 'derivation',
      };
    }
    return {
      status: 'inactive',
      reason: `The ${breach.statement_date ?? breach.cycle_end_date} statement was not paid in full by its due date. Interest accrues on new purchases from the transaction date until this account's grace period is restored.`,
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  // 6. No breach on record
  const last = newestClosed;
  if (last.statement_balance_cents === null) {
    return {
      status: 'unknown',
      reason: 'The most recent statement balance has not been recorded.',
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  const lastPaid = paidInFull(last, input);
  if (last.statement_balance_cents > 0n && lastPaid === true) {
    return {
      status: 'active',
      reason: `Statement dated ${last.statement_date ?? last.cycle_end_date} paid in full by ${last.payment_due_date}.`,
      paid_in_full_due_date: last.payment_due_date,
      derivedFrom: 'derivation',
    };
  }
  if (last.statement_balance_cents > 0n && lastPaid === null) {
    return {
      status: 'unknown',
      reason: `Whether the ${last.statement_date ?? last.cycle_end_date} statement was paid in full by its due date has not been recorded.`,
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }
  if (last.statement_balance_cents <= 0n) {
    const prior = closed[1];
    if (
      prior !== undefined &&
      ((prior.statement_balance_cents !== null && prior.statement_balance_cents <= 0n) ||
        paidInFull(prior, input) === true)
    ) {
      return {
        status: 'active',
        reason: 'No balance carried across the last two statements.',
        paid_in_full_due_date: null,
        derivedFrom: 'derivation',
      };
    }
    return {
      status: 'likely_active',
      reason: `The ${last.statement_date ?? last.cycle_end_date} statement showed no balance, but the prior cycle is unverified.`,
      paid_in_full_due_date: null,
      derivedFrom: 'derivation',
    };
  }

  return {
    status: 'unknown',
    reason: 'Grace status could not be derived from the available statement history.',
    paid_in_full_due_date: null,
    derivedFrom: 'derivation',
  };
}

function breachObligationPaid(
  breach: StatementCycleInput,
  closed: StatementCycleInput[],
  input: DeriveGraceInput,
): boolean {
  // Payoff confirmed: no remaining obligation on the breach cycle and no unpaid issued
  // statement obligations remain; satisfying payments are posted.
  if (breach.statement_balance_cents === null || breach.payment_due_date === null) {
    return false;
  }
  let allocated = 0n;
  for (const al of input.allocations) {
    if (al.deleted_at !== null || al.statement_cycle_id !== breach.id) continue;
    const pay = input.payments.find((p) => p.id === al.payment_id && p.deleted_at === null);
    if (!pay || pay.status !== 'posted') continue;
    allocated += al.amount_cents;
  }
  if (allocated < breach.statement_balance_cents) return false;

  for (const cycle of closed) {
    if (cycle.statement_balance_cents === null || cycle.statement_balance_cents <= 0n) continue;
    if (!['statement_issued', 'payment_due', 'past_due', 'partially_paid'].includes(cycle.status)) {
      // paid / reconciled / closed with payoff are ok if paid_in_full is true
      if (paidInFull(cycle, input) === true) continue;
      if (cycle.status === 'paid' || cycle.status === 'reconciled') continue;
    }
    if (paidInFull(cycle, input) === false) return false;
  }
  return true;
}

function countQualifyingAfter(
  breach: StatementCycleInput,
  closed: StatementCycleInput[],
  input: DeriveGraceInput,
): number {
  let n = 0;
  for (const cycle of closed) {
    if (
      compareBusinessDates(
        businessDate(cycle.cycle_end_date),
        businessDate(breach.cycle_end_date),
      ) <= 0
    ) {
      continue;
    }
    if (paidInFull(cycle, input) === true) n += 1;
  }
  return n;
}

function cyclesClosedSincePayoff(
  breach: StatementCycleInput,
  closed: StatementCycleInput[],
  input: DeriveGraceInput,
): number {
  // Use latest posted payment allocated to the breach as payoff date.
  let payoffDate: BusinessDate | null = null;
  for (const al of input.allocations) {
    if (al.deleted_at !== null || al.statement_cycle_id !== breach.id) continue;
    const pay = input.payments.find((p) => p.id === al.payment_id && p.deleted_at === null);
    if (!pay || pay.status !== 'posted' || pay.posted_date === null) continue;
    const d = businessDate(pay.posted_date);
    if (payoffDate === null || compareBusinessDates(d, payoffDate) > 0) payoffDate = d;
  }
  if (payoffDate === null) return 0;
  let n = 0;
  for (const cycle of closed) {
    if (compareBusinessDates(businessDate(cycle.cycle_end_date), payoffDate) > 0) n += 1;
  }
  return n;
}

function isRestored(
  rule: GraceRestoreRule,
  payoffConfirmed: boolean,
  breach: StatementCycleInput,
  closed: StatementCycleInput[],
  input: DeriveGraceInput,
): boolean {
  const qualifying = countQualifyingAfter(breach, closed, input);
  const sincePayoff = cyclesClosedSincePayoff(breach, closed, input);
  switch (rule) {
    case 'immediate_on_full_payoff':
      return payoffConfirmed;
    case 'next_billing_cycle_after_full_payoff':
      return payoffConfirmed && sincePayoff >= 1;
    case 'after_one_full_statement_cycle':
      return payoffConfirmed && qualifying >= 1;
    case 'after_two_full_statement_cycles':
      return payoffConfirmed && qualifying >= 2;
    case 'issuer_confirmation_required':
    case 'unknown':
      return false;
  }
}
